/**
 * Axios Configuration with Token-Based Authentication
 *
 * Configures axios interceptors to:
 * - Automatically add Authorization header with Bearer token
 * - Handle 401 responses with automatic token refresh
 * - Remove cookie-based authentication (withCredentials)
 */

import axios from 'axios';
import tokenManager from '../services/tokenManager';
import authService from '../services/authService';

let isRefreshing = false;
let failedQueue = [];
let refreshPromise = null; // Track the ongoing refresh promise
let initializationComplete = false; // Track if app initialization is complete
let initializationStarted = false; // Track if initialization has started (to prevent premature requests)
let isImpersonating = false; // Track if impersonation is in progress

/**
 * Start a token refresh and return the promise
 * This can be called from anywhere (saga or interceptor) to ensure single refresh
 * Returns: { accessToken, user } or throws error
 */
export const startTokenRefresh = (store) => {
  // If refresh is already in progress, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Start new refresh
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // Attempt to refresh the access token
      // Refresh token is sent automatically via httpOnly cookie
      // console.log('[Token Refresh] Starting token refresh...');
      const response = await authService.refreshToken();
      const { accessToken, user } = response;

      if (!accessToken) {
        throw new Error('Refresh token response missing accessToken');
      }

      // Store new access token
      tokenManager.setTokens(accessToken);
      // console.log('[Token Refresh] Token refresh successful');

      // Process all queued requests with new token
      processQueue(null, accessToken);

      isRefreshing = false;
      refreshPromise = null;

      return { accessToken, user };
    } catch (refreshError) {
      // Refresh failed (likely 401 - no refresh token cookie)
      // Mark initialization complete immediately so requests can proceed
      console.error('[Token Refresh] Token refresh failed:', refreshError);

      // If refresh failed immediately (e.g., 401), mark initialization complete
      // This allows public endpoints to proceed without waiting
      if (!initializationComplete) {
        markInitializationComplete();
        // console.log('[Token Refresh] Initialization marked complete due to refresh failure');
      }

      processQueue(refreshError, null);
      tokenManager.clearTokens();

      // Note: Don't dispatch here - the saga will handle it when it catches the error
      // Dispatching here with null store would fail silently

      isRefreshing = false;
      refreshPromise = null;

      throw refreshError;
    }
  })();

  return refreshPromise;
};

/**
 * Process queued requests after token refresh
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const clearImpersonationStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  window.sessionStorage.removeItem('impersonationAccessToken');
  window.sessionStorage.removeItem('impersonationUser');
};

/**
 * Configure axios request interceptor
 * Adds Authorization header to all requests
 */
export const setupRequestInterceptor = (store) => {
  axios.interceptors.request.use(
    async config => {
      // Skip auth endpoints - they don't need tokens
      const isAuthEndpoint = config.url && (
        config.url.includes('/api/auth/') ||
        config.url.includes('/api/login_check') ||
        config.url.includes('/api/logout')
      );

      // Skip non-API requests (static assets, sounds, etc.)
      const isApiRequest = config.url && config.url.includes('/api/');

      // Get current token
      let token = tokenManager.getAccessToken();

      // Wait for token refresh if one is in progress (both during and after initialization)
      // This prevents requests from going out without Authorization headers during token refresh
      if (!isAuthEndpoint && isApiRequest && refreshPromise) {
        // console.log(`[Request Interceptor] Token refresh in progress, waiting. Request: ${config.url}`);
        try {
          const refreshResult = await refreshPromise;
          if (refreshResult?.accessToken) {
            token = refreshResult.accessToken;
          }
        } catch (e) {
          // console.log(`[Request Interceptor] Token refresh failed, proceeding without token: ${config.url}`);
          // swallow; will proceed without token and hit 401 handler
        }
        // Get token after refresh completes
        token = token || tokenManager.getAccessToken();
      }

      // During initialization: wait for it to complete (unless refresh is already in progress above)
      if (!isAuthEndpoint && isApiRequest && !initializationComplete && !refreshPromise) {
        // console.log(`[Request Interceptor] API request during initialization, waiting. Request: ${config.url}`);

        // Wait for initialization to complete (up to 8s) or a refreshPromise to appear
        const maxWaitMs = 8000;
        const stepMs = 100;
        let waitedMs = 0;

        while (!initializationComplete && waitedMs < maxWaitMs && !refreshPromise) {
          await new Promise(resolve => setTimeout(resolve, stepMs));
          waitedMs += stepMs;
        }

        // If a refresh started during waiting, await it
        if (refreshPromise) {
          try {
            const refreshResult = await refreshPromise;
            if (refreshResult?.accessToken) {
              token = refreshResult.accessToken;
            }
          } catch (e) {
            // swallow; will proceed without token and hit 401 handler
          }
        }

        // Get token after waiting
        token = token || tokenManager.getAccessToken();

        // if (!initializationComplete) {
        //   console.log(`[Request Interceptor] Initialization timeout, proceeding anyway: ${config.url}, hasToken: ${!!token}`);
        // } else {
        //   console.log(`[Request Interceptor] Initialization complete, proceeding: ${config.url}, hasToken: ${!!token}`);
        // }
      }

      // Add token to request if available
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // else if (!isAuthEndpoint) {
      //   console.log(`[Request Interceptor] No token available for ${config.url}, proceeding without Authorization header`);
      // }

      // Set withCredentials based on endpoint
      // Auth endpoints need cookies for refresh token
      const needsCredentials = config.url && (
        config.url.includes('/api/auth/') ||
        config.url.includes('/api/login_check') ||
        config.url.includes('/api/logout')
      );

      // Explicitly set withCredentials for auth endpoints, false for others
      if (config.withCredentials === undefined) {
        config.withCredentials = needsCredentials;
      }

      // console.log(`[Request Interceptor] Proceeding with request: ${config.url}, hasToken: ${!!token}, initComplete: ${initializationComplete}`);
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );
};

/**
 * Configure axios response interceptor
 * Handles 401 errors with automatic token refresh
 */
export const setupResponseInterceptor = (store) => {
  axios.interceptors.response.use(
    response => {
      // Check for new access token in response headers (from user impersonation)
      // This header is set by SwitchUserSubscriber when user switches
      const newAccessToken = response.headers['x-new-access-token'] || response.headers['X-New-Access-Token'];
      if (newAccessToken) {
        // console.log('[Response Interceptor] New access token received (user switch detected), updating token');
        tokenManager.setTokens(newAccessToken);
        // Update global impersonation flag for UI (exit button, etc.)
        if (typeof window !== 'undefined') {
          window.isImpersonating = tokenManager.isImpersonated();
        }

        // Token is already updated, no need to fetch user data separately
        // The token contains all necessary user information in its payload
      }

      return response;
    },
    async error => {
      const originalRequest = error.config;

      // Handle 401 Unauthorized responses
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Avoid deadlock when the refresh request itself fails.
        // We must let startTokenRefresh catch the rejection instead of queueing.
        const isRefreshRequest = originalRequest?.url?.includes('/api/auth/refresh');
        if (isRefreshRequest) {
          return Promise.reject(error);
        }

        // Don't try to refresh token for failed login attempts
        // Let the login form show the proper error message
        const isLoginRequest = originalRequest?.url?.includes('/api/login_check');
        if (isLoginRequest) {
          return Promise.reject(error);
        }

        // Check if current token is an impersonation token that expired
        const currentToken = tokenManager.getAccessToken();
        let isImpersonated = false;
        try {
          isImpersonated = currentToken ? tokenManager.isImpersonated() : false;
        } catch (e) {
          // If token is invalid or can't be decoded, assume not impersonated
          console.warn('[Response Interceptor] Error checking impersonation status:', e);
          isImpersonated = false;
        }

        if (isImpersonated) {
          // Impersonation token expired - exit impersonation and get original user's token
          // console.log('[Response Interceptor] Impersonation token expired, exiting impersonation');
          originalRequest._retry = true;

          try {
            const exitResponse = await authService.exitImpersonation();

            if (exitResponse.success && exitResponse.accessToken && exitResponse.user) {
              // Update token with original user's token
              tokenManager.setTokens(exitResponse.accessToken);
              if (typeof window !== 'undefined') {
                window.isImpersonating = tokenManager.isImpersonated();
              }
              clearImpersonationStorage();

              // Update user state if store is available
              if (store) {
                store.dispatch({
                  type: 'FETCH_CREDENTIALS_FULFILLED',
                  payload: { data: exitResponse.user },
                });

                // Show notification that impersonation ended
                if (store.dispatch && typeof store.dispatch === 'function') {
                  // You can dispatch a notification action here if you have one
                  // console.log('[Response Interceptor] Impersonation expired, switched back to original user');
                }
              }

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${exitResponse.accessToken}`;
              return axios(originalRequest);
            } else {
              throw new Error('Failed to exit impersonation');
            }
          } catch (exitError) {
            console.error('[Response Interceptor] Failed to exit impersonation:', exitError);
            // If exit fails, redirect to login
            return Promise.reject(exitError);
          }
        }

        // Check if impersonation is in progress (URL parameter) - if so, don't use refresh token endpoint
        // The app initialization saga will handle getting tokens via refresh token endpoint
        const urlParams = new URLSearchParams(window.location.search);
        const switchUser = urlParams.get('_switch_user');
        const isImpersonation = switchUser && switchUser !== '_exit';

        if (isImpersonation && !initializationComplete) {
          // console.log('[Response Interceptor] Impersonation in progress, queueing request until impersonation completes');
          // Queue this request to be retried after impersonation completes
          return new Promise((resolve, reject) => {
            // Wait for initialization to complete, then retry
            const checkInterval = setInterval(() => {
              if (initializationComplete) {
                clearInterval(checkInterval);
                // Retry the request with the new token
                const token = tokenManager.getAccessToken();
                if (token) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(axios(originalRequest));
                } else {
                  reject(new Error('No token available after impersonation'));
                }
              }
            }, 100);

            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkInterval);
              reject(new Error('Timeout waiting for impersonation to complete'));
            }, 5000);
          });
        }

        // Check if we're already refreshing to avoid multiple refresh calls
        if (isRefreshing) {
          // Queue this request to be retried after refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;

        // Use shared refresh function (handles duplicate calls automatically)
        try {
          const { accessToken } = await startTokenRefresh(store);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      // Handle 403 Forbidden - this is an authorization (permission) issue, not authentication.
      // Do NOT force logout; the user is authenticated but lacks specific permissions.
      // Let the calling code handle the 403 rejection with appropriate UI feedback.

      return Promise.reject(error);
    }
  );
};

/**
 * Mark initialization as started (called when app initialization begins)
 */
export const markInitializationStarted = () => {
  initializationStarted = true;
};

/**
 * Mark initialization as complete (called after token refresh in app initialization)
 */
export const markInitializationComplete = () => {
  initializationComplete = true;
};

/**
 * Initialize all axios configurations
 * @param {Object} store - Redux store instance
 */
export const setupAxiosInterceptors = (store) => {
  setupRequestInterceptor(store);
  setupResponseInterceptor(store);

  // Set up automatic token refresh callback
  // Refresh token is automatically sent via httpOnly cookie
  tokenManager.setRefreshCallback(async () => {
    return authService.refreshToken();
  });

  // console.log('Axios interceptors configured for token-based authentication');
};

/**
 * Remove all axios interceptors (useful for cleanup/testing)
 */
export const removeAxiosInterceptors = () => {
  axios.interceptors.request.handlers = [];
  axios.interceptors.response.handlers = [];
  tokenManager.setRefreshCallback(null);
};
